import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    loadStripe
} from '@stripe/stripe-js';

import {
    FaArrowLeft,
    FaBroadcastTower,
    FaEye,
    FaSignOutAlt,
    FaHeart,
    FaGift,
    FaCommentDots,
    FaPaperPlane,
    FaTimes,
    FaMicrophone,
    FaMicrophoneSlash,
    FaVideo,
    FaVideoSlash
} from 'react-icons/fa';

import {
    useNavigate,
    useParams
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

import '../css/LiveViewerPage.css';


const RTC_CONFIGURATION = {
    iceServers: [
        {
            urls: 'stun:stun.l.google.com:19302'
        },
        {
            urls: 'stun:stun1.l.google.com:19302'
        },
        {
            urls: 'stun:stun2.l.google.com:19302'
        }
    ]
};


const stripePromise =
    loadStripe(
        'pk_live_51RaLovG1ayf6yxIseClJKLKIICISLsy7qsI0HRb6CICjVQxgf2SHBJUbz41qn6GXAhS3YXeOphh6KQZ3jbBorWJs009zrBj6g8'
        //'pk_test_51RaLpQQI0DRZZizL9l13ZhsmQVY7uovpXAYB654Ll681oKpDY2DTbTvy5DnkwAHektsOLiHsE2XQNNvcMIm9d3WV00slgNFjlJ'
    );


const LIVE_GIFTS = [

    {
        code: 'rose',
        emoji: '🌹',
        label: 'Rose',
        price: 10
    },

    {
        code: 'love',
        emoji: '💖',
        label: 'Love',
        price: 25
    },

    {
        code: 'star',
        emoji: '⭐',
        label: 'Star',
        price: 50
    },

    {
        code: 'diamond',
        emoji: '💎',
        label: 'Diamond',
        price: 100
    },

    {
        code: 'crown',
        emoji: '👑',
        label: 'Crown',
        price: 250
    },

    {
        code: 'ring',
        emoji: '💍',
        label: 'Ring',
        price: 500
    }
];


const COIN_PACKAGES = [

    {
        code: 'COINS_100',
        coins: 100,
        price: '$1.99'
    },

    {
        code: 'COINS_500',
        coins: 500,
        price: '$7.99'
    },

    {
        code: 'COINS_1200',
        coins: 1200,
        price: '$14.99'
    },

    {
        code: 'COINS_3000',
        coins: 3000,
        price: '$29.99'
    }

];


function LiveViewerPage() {

    const { liveId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [live, setLive] =
        useState(null);


    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState('');

    const [videoConnected, setVideoConnected] =
        useState(false);

    const [needsTapToPlay, setNeedsTapToPlay] =
        useState(false);

    const [viewerCount, setViewerCount] =
        useState(0);

    const [liveReactions, setLiveReactions] =
        useState([]);

    const [guestRequestStatus, setGuestRequestStatus] =
        useState('idle');

    // idle | pending | accepted | rejected

    const [, setGuestMediaLoading] =
        useState(false);

    const [, setGuestMediaError] =
        useState('');

    const guestStreamRef =
        useRef(null);

    const stompWasDisconnectedRef =
        useRef(false);

    const [coinBalance, setCoinBalance] =
        useState(0);

    const [walletLoading, setWalletLoading] =
        useState(false);

    const [showGiftPicker, setShowGiftPicker] =
        useState(false);

    const [showCoinModal, setShowCoinModal] =
        useState(false);


    const [sendingGiftCode, setSendingGiftCode] =
        useState(null);

    const [buyingPackage, setBuyingPackage] =
        useState(null);

    const [giftError, setGiftError] =
        useState('');

    const [liveGifts, setLiveGifts] =
        useState([]);

    const [commentText, setCommentText] =
        useState('');

    const [liveComments, setLiveComments] =
        useState([]);


    const stompClientRef =
        useRef(null);

    const peerConnectionRef =
        useRef(null);

    const remoteVideoRef =
        useRef(null);

    const remoteStreamRef =
        useRef(null);

    const guestLocalVideoRef =
        useRef(null);

    const guestPreviewRef =
        useRef(null);

    const liveVideoContainerRef =
        useRef(null);

    const dragDataRef =
        useRef({
            offsetX: 0,
            offsetY: 0
        });

    const [guestPreviewPosition, setGuestPreviewPosition] =
        useState({
            x: null,
            y: null
        });

    const [guestPreviewDragging, setGuestPreviewDragging] =
        useState(false);

    const [guestMicEnabled, setGuestMicEnabled] =
        useState(true);

    const [guestCameraEnabled, setGuestCameraEnabled] =
        useState(true);

    const pendingCandidatesRef =
        useRef([]);


    const token =
        localStorage.getItem('authToken');


    const authorizationHeader =
        useMemo(() => {

            if (!token) {
                return null;
            }

            return token.startsWith('Bearer ')
                ? token
                : `Bearer ${token}`;

        }, [token]);


    const currentUser =
        useMemo(() => {

            try {

                const stored =
                    localStorage.getItem(
                        'currentUser'
                    );

                return stored
                    ? JSON.parse(stored)
                    : null;

            } catch (error) {

                console.error(
                    'Unable to read current user:',
                    error
                );

                return null;
            }

        }, []);





    const handleLeaveLive =
        useCallback(() => {

            console.log(
                '👋 Viewer leaving live'
            );

            navigate(
                '/live'
            );

        }, [navigate]);

    const addLiveReaction =
        useCallback((userName) => {

            const reaction = {
                id:
                    `${Date.now()}-${Math.random()}`,

                name:
                    userName || 'User',

                left:
                    5 + Math.random() * 25,

                duration:
                    1.8 + Math.random() * 0.8
            };


            setLiveReactions(
                (previous) => [
                    ...previous,
                    reaction
                ]
            );


            window.setTimeout(
                () => {

                    setLiveReactions(
                        (previous) =>
                            previous.filter(
                                (item) =>
                                    item.id !==
                                    reaction.id
                            )
                    );

                },
                3000
            );

        }, []);


    const addLiveComment =
        useCallback((comment) => {

            const item = {
                id:
                    `${Date.now()}-${Math.random()}`,

                userName:
                    comment.userName || 'User',

                message:
                    comment.message || ''
            };


            setLiveComments(
                previous => [
                    ...previous.slice(-4),
                    item
                ]
            );


            window.setTimeout(
                () => {

                    setLiveComments(
                        previous =>
                            previous.filter(
                                value =>
                                    value.id !== item.id
                            )
                    );

                },
                10000
            );

        }, []);



    const handleBuyCoins =
        useCallback(
            async (coinPackage) => {

                if (
                    !live?.id ||
                    !authorizationHeader ||
                    buyingPackage
                ) {
                    return;
                }


                try {

                    setBuyingPackage(
                        coinPackage.code
                    );


                    const params =
                        new URLSearchParams({

                            packageCode:
                                coinPackage.code,

                            liveId:
                                String(
                                    live.id
                                )
                        });


                    const response =
                        await fetch(

                            `${process.env.REACT_APP_API_URL}/api/payment/create-coin-checkout-session?${params.toString()}`,

                            {
                                method: 'POST',

                                headers: {
                                    Authorization:
                                        authorizationHeader
                                }
                            }
                        );


                    const result =
                        await response
                            .json()
                            .catch(() => null);


                    if (!response.ok) {

                        throw new Error(
                            result?.message ||
                            'Unable to start payment.'
                        );
                    }


                    if (!result?.id) {

                        throw new Error(
                            'Stripe session was not created.'
                        );
                    }


                    const stripe =
                        await stripePromise;


                    if (!stripe) {

                        throw new Error(
                            'Stripe is unavailable.'
                        );
                    }


                    await stripe
                        .redirectToCheckout({
                            sessionId:
                                result.id
                        });


                } catch (error) {

                    console.error(
                        '❌ Stripe coin payment error:',
                        error
                    );


                    setGiftError(
                        error?.message ||
                        'Unable to start coin payment.'
                    );


                } finally {

                    setBuyingPackage(
                        null
                    );
                }

            },
            [
                live?.id,
                authorizationHeader,
                buyingPackage
            ]
        );

    const handleSendComment =
        useCallback((event) => {

            event?.preventDefault();


            const message =
                commentText.trim();


            if (
                !message ||
                !live?.id ||
                !currentUser?.id
            ) {

                return;
            }


            const userName =
                currentUser?.name ||
                currentUser?.username ||
                'User';


            /*
             * Show immediately
             * for sender.
             */
            addLiveComment({
                userName,
                message
            });


            if (
                stompClientRef.current
                    ?.connected
            ) {

                stompClientRef.current.publish({

                    destination:
                        `/app/live/${live.id}`,

                    body:
                        JSON.stringify({

                            liveId:
                                live.id,

                            fromUserId:
                                currentUser.id,

                            fromUserName:
                                userName,

                            type:
                                'LIVE_COMMENT',

                            message:
                                message
                        })
                });

            }


            setCommentText('');

        }, [
            commentText,
            live?.id,
            currentUser?.id,
            currentUser?.name,
            currentUser?.username,
            addLiveComment
        ]);

    const addLiveGift =
        useCallback(
            ({
                userName,
                emoji,
                label
            }) => {

                const gift = {

                    id:
                        `${Date.now()}-${Math.random()}`,

                    userName:
                        userName || 'User',

                    emoji:
                        emoji || '🎁',

                    label:
                        label || 'Gift',

                    left:
                        28 + Math.random() * 40
                };


                setLiveGifts(
                    previous => [
                        ...previous.slice(-3),
                        gift
                    ]
                );


                window.setTimeout(
                    () => {

                        setLiveGifts(
                            previous =>
                                previous.filter(
                                    item =>
                                        item.id !==
                                        gift.id
                                )
                        );

                    },
                    3800
                );

            },
            []
        );


    const handleSendGift =
        useCallback(
            async (gift) => {

                if (
                    !live?.id ||
                    !authorizationHeader ||
                    sendingGiftCode
                ) {
                    return;
                }


                try {

                    setSendingGiftCode(
                        gift.code
                    );

                    setGiftError('');


                    // =====================================
                    // BACKEND CHARGES WALLET
                    // =====================================

                    const response =
                        await fetch(

                            `${process.env.REACT_APP_API_URL}/api/live/${live.id}/gifts/send`,

                            {
                                method: 'POST',

                                headers: {

                                    'Content-Type':
                                        'application/json',

                                    Authorization:
                                        authorizationHeader
                                },

                                body:
                                    JSON.stringify({
                                        giftCode:
                                            gift.code
                                    })
                            }
                        );


                    const result =
                        await response
                            .json()
                            .catch(() => null);


                    // =====================================
                    // NOT ENOUGH COINS
                    // =====================================

                    if (
                        response.status === 409 &&
                        result?.code ===
                        'INSUFFICIENT_COINS'
                    ) {

                        setCoinBalance(
                            Number(
                                result?.balance || 0
                            )
                        );


                        setGiftError(
                            `You need ${result.required} coins. Your balance is ${result.balance}.`
                        );


                        /*
                         * Open Stripe coin purchase modal.
                         */
                        setShowCoinModal(
                            true
                        );


                        return;
                    }


                    if (!response.ok) {

                        throw new Error(
                            result?.message ||
                            'Unable to send gift.'
                        );
                    }


                    // =====================================
                    // PAYMENT SUCCESS
                    // =====================================

                    setCoinBalance(
                        Number(
                            result.remainingBalance
                        )
                    );


                    const myName =
                        currentUser?.name ||
                        currentUser?.username ||
                        'User';


                    /*
                     * Show immediately for sender.
                     *
                     * DO NOT publish LIVE_GIFT through
                     * STOMP here.
                     *
                     * Spring Boot already broadcasts it.
                     */
                    addLiveGift({

                        userName:
                            myName,

                        emoji:
                            result.giftEmoji,

                        label:
                            result.giftLabel
                    });


                    setShowGiftPicker(
                        false
                    );


                    if (
                        navigator.vibrate
                    ) {

                        navigator.vibrate(
                            30
                        );
                    }


                } catch (error) {

                    console.error(
                        '❌ Live gift error:',
                        error
                    );


                    setGiftError(
                        error?.message ||
                        'Unable to send gift.'
                    );


                } finally {

                    setSendingGiftCode(
                        null
                    );
                }

            },
            [
                live?.id,
                authorizationHeader,
                sendingGiftCode,
                currentUser?.name,
                currentUser?.username,
                addLiveGift
            ]
        );

    const handleSendHeart =
        useCallback(() => {

            if (
                !live?.id ||
                !currentUser?.id
            ) {
                return;
            }


            /*
             * Show immediately
             * on sender screen
             */
            const myName =
                currentUser?.name ||
                currentUser?.username ||
                'User';

            addLiveReaction(
                myName
            );


            /*
             * Send to host +
             * other viewers
             */
            if (
                stompClientRef.current
                    ?.connected
            ) {

                stompClientRef.current
                    .publish({

                        destination:
                            `/app/live/${live.id}`,

                        body:
                            JSON.stringify({

                                liveId:
                                    live.id,

                                fromUserId:
                                    currentUser.id,

                                fromUserName:
                                    currentUser?.name ||
                                    currentUser?.username ||
                                    'User',

                                type:
                                    'REACTION_HEART'

                            })
                    });
            }


            if (
                navigator.vibrate
            ) {
                navigator.vibrate(20);
            }

        }, [
            live?.id,
            currentUser?.id,
            addLiveReaction,
            currentUser?.name,
            currentUser?.username,
        ]);


    const fetchCoinBalance =
        useCallback(
            async () => {

                if (!authorizationHeader) {
                    return;
                }


                try {

                    setWalletLoading(true);


                    const response =
                        await fetch(
                            `${process.env.REACT_APP_API_URL}/api/wallet/balance`,
                            {
                                headers: {
                                    Authorization:
                                        authorizationHeader
                                }
                            }
                        );


                    const data =
                        await response
                            .json()
                            .catch(() => null);


                    if (!response.ok) {

                        console.error(
                            'Unable to load wallet:',
                            data
                        );

                        return;
                    }


                    setCoinBalance(
                        Number(
                            data?.balance || 0
                        )
                    );


                } catch (error) {

                    console.error(
                        '❌ Wallet balance error:',
                        error
                    );


                } finally {

                    setWalletLoading(false);
                }

            },
            [
                authorizationHeader
            ]
        );


    // =====================================
    // LOAD LIVE INFORMATION
    // =====================================

    const fetchLive =
        useCallback(async () => {

            try {

                const response =
                    await fetch(

                        `${process.env.REACT_APP_API_URL}/api/live/${liveId}`,

                        {
                            headers:
                                authorizationHeader
                                    ? {
                                        Authorization:
                                            authorizationHeader
                                    }
                                    : {}
                        }
                    );


                const data =
                    await response
                        .json()
                        .catch(() => null);


                if (!response.ok) {

                    throw new Error(
                        data?.message ||
                        'Unable to load this live.'
                    );
                }


                setLive(data);
                setError('');


            } catch (fetchError) {

                console.error(
                    'Unable to load live:',
                    fetchError
                );


                setError(
                    fetchError?.message ||
                    t(
                        'live.viewerLoadError',
                        'Unable to load this live.'
                    )
                );


            } finally {

                setLoading(false);
            }

        }, [
            authorizationHeader,
            liveId,
            t
        ]);


    const startRemotePlayback =
        useCallback(async () => {

            const video =
                remoteVideoRef.current;

            if (
                !video ||
                !video.srcObject
            ) {
                return;
            }

            try {

                video.muted = false;

                await video.play();

                setVideoConnected(true);
                setNeedsTapToPlay(false);

                console.log(
                    '✅ Remote Live video/audio playing'
                );

            } catch (error) {

                console.warn(
                    '⚠️ Unmuted playback blocked:',
                    error
                );

                /*
                 * On iPhone, try to at least
                 * display the video muted.
                 */
                try {

                    video.muted = true;

                    await video.play();

                    setVideoConnected(true);
                    setNeedsTapToPlay(true);

                    console.log(
                        '🔇 Live video playing muted - tap required for audio'
                    );

                } catch (secondError) {

                    console.error(
                        '❌ Remote Live playback failed:',
                        secondError
                    );

                    setVideoConnected(false);
                    setNeedsTapToPlay(true);
                }
            }

        }, []);


    const handleRequestToJoin =
        useCallback(() => {

            if (
                !live?.id ||
                !live?.hostId ||
                !currentUser?.id ||
                !stompClientRef.current?.connected ||
                guestRequestStatus === 'pending' ||
                guestRequestStatus === 'accepted'
            ) {
                return;
            }

            setGuestRequestStatus(
                'pending'
            );

            setGuestMediaError('');

            stompClientRef.current.publish({

                destination:
                    `/app/live/${live.id}`,

                body:
                    JSON.stringify({

                        liveId:
                            live.id,

                        fromUserId:
                            currentUser.id,

                        fromUserName:
                            currentUser?.name ||
                            currentUser?.username ||
                            'User',

                        toUserId:
                            live.hostId,

                        type:
                            'GUEST_REQUEST'

                    })
            });

            console.log(
                '🙋 Guest request sent'
            );

        }, [
            live?.id,
            live?.hostId,
            currentUser?.id,
            currentUser?.name,
            currentUser?.username,
            guestRequestStatus
        ]);

    useEffect(() => {

        fetchCoinBalance();

    }, [fetchCoinBalance]);


    useEffect(() => {

        const params =
            new URLSearchParams(
                window.location.search
            );


        const coinStatus =
            params.get('coins');


        if (
            coinStatus === 'success'
        ) {

            /*
             * The webhook may take a moment
             * to update the database.
             */
            fetchCoinBalance();


            window.setTimeout(
                fetchCoinBalance,
                1500
            );


            window.setTimeout(
                fetchCoinBalance,
                3000
            );


            /*
             * Remove Stripe query parameters
             * from URL.
             */
            window.history.replaceState(
                {},
                '',
                window.location.pathname
            );
        }


        if (
            coinStatus === 'cancelled'
        ) {

            window.history.replaceState(
                {},
                '',
                window.location.pathname
            );
        }

    }, [fetchCoinBalance]);


    // =====================================
    // REFRESH LIVE STATUS
    // =====================================

    useEffect(() => {

        fetchLive();


        const interval =
            window.setInterval(
                fetchLive,
                5000
            );


        return () => {

            window.clearInterval(
                interval
            );
        };

    }, [fetchLive]);



    const startGuestMedia =
        useCallback(async () => {

            if (
                guestStreamRef.current
            ) {
                return guestStreamRef.current;
            }


            try {

                setGuestMediaLoading(true);
                setGuestMediaError('');


                if (
                    !navigator.mediaDevices
                        ?.getUserMedia
                ) {

                    throw new Error(
                        t(
                            'live.mediaNotSupported',
                            'Camera and microphone are not supported on this device.'
                        )
                    );
                }


                const stream =
                    await navigator
                        .mediaDevices
                        .getUserMedia({

                            video: {

                                facingMode:
                                    'user',

                                width: {
                                    ideal: 720
                                },

                                height: {
                                    ideal: 1280
                                }
                            },

                            audio: {
                                echoCancellation: true,
                                noiseSuppression: true,
                                autoGainControl: true
                            }
                        });


                guestStreamRef.current =
                    stream;


                if (
                    guestLocalVideoRef.current
                ) {

                    guestLocalVideoRef.current
                        .srcObject =
                        stream;

                    guestLocalVideoRef.current
                        .muted =
                        true;

                    try {

                        await guestLocalVideoRef
                            .current
                            .play();

                    } catch (error) {

                        console.warn(
                            'Unable to start guest preview:',
                            error
                        );
                    }
                }


                setGuestCameraEnabled(
                    true
                );

                setGuestMicEnabled(
                    true
                );


                return stream;


            } catch (error) {

                console.error(
                    '❌ Guest media error:',
                    error
                );


                setGuestMediaError(
                    error?.message ||
                    t(
                        'live.guestMediaError',
                        'Unable to access your camera and microphone.'
                    )
                );


                return null;


            } finally {

                setGuestMediaLoading(
                    false
                );
            }

        }, [t]);


    const handleToggleGuestMicrophone =
        useCallback(() => {

            const stream =
                guestStreamRef.current;

            if (!stream) {
                return;
            }

            const tracks =
                stream.getAudioTracks();

            if (!tracks.length) {
                return;
            }

            const nextEnabled =
                !tracks.some(
                    track => track.enabled
                );

            tracks.forEach(
                track => {
                    track.enabled =
                        nextEnabled;
                }
            );

            setGuestMicEnabled(
                nextEnabled
            );

        }, []);


    const handleToggleGuestCamera =
        useCallback(() => {

            const stream =
                guestStreamRef.current;

            if (!stream) {
                return;
            }

            const tracks =
                stream.getVideoTracks();

            if (!tracks.length) {
                return;
            }

            const nextEnabled =
                !tracks.some(
                    track => track.enabled
                );

            tracks.forEach(
                track => {
                    track.enabled =
                        nextEnabled;
                }
            );

            setGuestCameraEnabled(
                nextEnabled
            );

        }, []);


    const handleLeaveGuestMode =
        useCallback(async () => {

            const peer =
                peerConnectionRef.current;

            const stream =
                guestStreamRef.current;


            if (
                peer &&
                stream
            ) {

                stream
                    .getTracks()
                    .forEach(track => {

                        const sender =
                            peer
                                .getSenders()
                                .find(
                                    item =>
                                        item.track ===
                                        track
                                );

                        if (sender) {

                            try {
                                peer.removeTrack(
                                    sender
                                );
                            } catch (error) {

                                console.warn(
                                    'Unable to remove guest track:',
                                    error
                                );
                            }
                        }


                        track.stop();
                    });


                guestStreamRef.current =
                    null;


                // Renegotiate without guest camera/mic
                try {

                    const offer =
                        await peer
                            .createOffer();


                    await peer
                        .setLocalDescription(
                            offer
                        );


                    if (
                        stompClientRef.current
                            ?.connected
                    ) {

                        stompClientRef.current
                            .publish({

                                destination:
                                    `/app/live/${live.id}`,

                                body:
                                    JSON.stringify({

                                        liveId:
                                            live.id,

                                        fromUserId:
                                            currentUser.id,

                                        toUserId:
                                            live.hostId,

                                        type:
                                            'GUEST_LEAVE_OFFER',

                                        sdp:
                                            peer
                                                .localDescription
                                                ?.sdp ||
                                            offer.sdp
                                    })
                            });
                    }

                } catch (error) {

                    console.error(
                        'Unable to renegotiate after leaving guest mode:',
                        error
                    );
                }
            }


            if (
                guestLocalVideoRef.current
            ) {

                guestLocalVideoRef.current
                    .srcObject =
                    null;
            }


            setGuestRequestStatus(
                'idle'
            );

            setGuestCameraEnabled(
                true
            );

            setGuestMicEnabled(
                true
            );


            if (
                stompClientRef.current
                    ?.connected
            ) {

                stompClientRef.current
                    .publish({

                        destination:
                            `/app/live/${live.id}`,

                        body:
                            JSON.stringify({

                                liveId:
                                    live.id,

                                fromUserId:
                                    currentUser.id,

                                toUserId:
                                    live.hostId,

                                type:
                                    'GUEST_LEFT'

                            })
                    });
            }


            console.log(
                '👋 Guest mode ended — viewer stays in live'
            );

        }, [
            live?.id,
            live?.hostId,
            currentUser?.id
        ]);


    const handleGuestPreviewDragStart =
        useCallback((clientX, clientY) => {

            const preview =
                guestPreviewRef.current;

            const container =
                liveVideoContainerRef.current;

            if (
                !preview ||
                !container
            ) {
                return;
            }

            const previewRect =
                preview.getBoundingClientRect();

            const containerRect =
                container.getBoundingClientRect();

            dragDataRef.current = {
                offsetX:
                    clientX -
                    previewRect.left,

                offsetY:
                    clientY -
                    previewRect.top
            };

            setGuestPreviewPosition({
                x:
                    previewRect.left -
                    containerRect.left,

                y:
                    previewRect.top -
                    containerRect.top
            });

            setGuestPreviewDragging(
                true
            );

        }, []);


    const handleGuestPreviewDragMove =
        useCallback((clientX, clientY) => {

            if (
                !guestPreviewDragging
            ) {
                return;
            }

            const preview =
                guestPreviewRef.current;

            const container =
                liveVideoContainerRef.current;

            if (
                !preview ||
                !container
            ) {
                return;
            }

            const containerRect =
                container.getBoundingClientRect();

            const previewRect =
                preview.getBoundingClientRect();

            const maxX =
                containerRect.width -
                previewRect.width;

            const maxY =
                containerRect.height -
                previewRect.height;

            let x =
                clientX -
                containerRect.left -
                dragDataRef.current.offsetX;

            let y =
                clientY -
                containerRect.top -
                dragDataRef.current.offsetY;


            x = Math.max(
                0,
                Math.min(
                    x,
                    maxX
                )
            );


            y = Math.max(
                0,
                Math.min(
                    y,
                    maxY
                )
            );


            setGuestPreviewPosition({
                x,
                y
            });

        }, [
            guestPreviewDragging
        ]);


    const handleGuestPreviewDragEnd =
        useCallback(() => {

            setGuestPreviewDragging(
                false
            );

        }, []);


    useEffect(() => {

        if (
            !guestPreviewDragging
        ) {
            return undefined;
        }


        const handleMouseMove =
            (event) => {

                handleGuestPreviewDragMove(
                    event.clientX,
                    event.clientY
                );
            };


        const handleMouseUp =
            () => {

                handleGuestPreviewDragEnd();
            };


        const handleTouchMove =
            (event) => {

                if (
                    !event.touches?.length
                ) {
                    return;
                }

                event.preventDefault();

                handleGuestPreviewDragMove(
                    event.touches[0].clientX,
                    event.touches[0].clientY
                );
            };


        const handleTouchEnd =
            () => {

                handleGuestPreviewDragEnd();
            };


        window.addEventListener(
            'mousemove',
            handleMouseMove
        );

        window.addEventListener(
            'mouseup',
            handleMouseUp
        );

        window.addEventListener(
            'touchmove',
            handleTouchMove,
            {
                passive: false
            }
        );

        window.addEventListener(
            'touchend',
            handleTouchEnd
        );


        return () => {

            window.removeEventListener(
                'mousemove',
                handleMouseMove
            );

            window.removeEventListener(
                'mouseup',
                handleMouseUp
            );

            window.removeEventListener(
                'touchmove',
                handleTouchMove
            );

            window.removeEventListener(
                'touchend',
                handleTouchEnd
            );
        };

    }, [
        guestPreviewDragging,
        handleGuestPreviewDragMove,
        handleGuestPreviewDragEnd
    ]);


    // =====================================
    // STOMP + WEBRTC
    // =====================================

    useEffect(() => {

        if (
            !live?.id ||
            String(
                live.status
            ).toUpperCase() !== 'LIVE' ||
            !live?.hostId ||
            !currentUser?.id ||
            !token ||
            !authorizationHeader
        ) {

            return undefined;
        }


        console.log(
            '🟡 Connecting viewer to live signaling:',
            live.id
        );




        const remoteVideoElement =
            remoteVideoRef.current;


        const socket =
            new SockJS(

                `${process.env.REACT_APP_API_URL}/ws?token=${encodeURIComponent(
                    token
                )}`
            );


        const client =
            new Client({

                webSocketFactory:
                    () => socket,

                connectHeaders: {
                    Authorization:
                        authorizationHeader
                },

                reconnectDelay:
                    5000,

                heartbeatIncoming:
                    4000,

                heartbeatOutgoing:
                    4000,



                onWebSocketClose: () => {

                    console.warn(
                        '🔌 Viewer WebSocket disconnected'
                    );

                    stompWasDisconnectedRef.current =
                        true;
                },


                onConnect: () => {

                    console.log(
                        '✅ Viewer connected to live WebSocket'
                    );


                    stompClientRef.current =
                        client;


                    stompWasDisconnectedRef.current =
                        false;


                    // =====================================
                    // LISTEN TO LIVE SIGNALS
                    // =====================================

                    client.subscribe(

                        `/topic/live/${live.id}`,

                        async (message) => {

                            try {

                                const payload =
                                    JSON.parse(
                                        message.body
                                    );


                                console.log(
                                    '📡 Viewer received live message:',
                                    payload
                                );

                                if (
                                    payload.type === 'END'
                                ) {

                                    console.log(
                                        '🛑 Live ended by host/admin'
                                    );

                                    navigate('/live');

                                    return;
                                }

                                // Ignore own messages
                                if (
                                    String(
                                        payload.fromUserId
                                    ) ===
                                    String(
                                        currentUser.id
                                    )
                                ) {

                                    return;
                                }


                                // Ignore messages for another viewer
                                if (
                                    payload.type !== 'END' &&
                                    payload.toUserId != null &&
                                    String(
                                        payload.toUserId
                                    ) !==
                                    String(
                                        currentUser.id
                                    )
                                ) {

                                    return;
                                }


                                // =====================================
                                // LIVE HEART REACTION
                                // =====================================

                                if (
                                    payload.type ===
                                    'REACTION_HEART'
                                ) {

                                    addLiveReaction(
                                        payload.fromUserName
                                    );

                                    console.log(
                                        '❤️ Live reaction received from:',
                                        payload.fromUserId
                                    );

                                    return;
                                }

                                // =====================================
                                // LIVE COMMENT
                                // =====================================

                                if (
                                    payload.type ===
                                    'LIVE_COMMENT'
                                ) {

                                    addLiveComment({

                                        userName:
                                            payload.fromUserName,

                                        message:
                                            payload.message

                                    });


                                    return;
                                }


                                // =====================================
                                // LIVE GIFT
                                // =====================================

                                if (
                                    payload.type ===
                                    'LIVE_GIFT'
                                ) {

                                    addLiveGift({

                                        userName:
                                            payload.fromUserName,

                                        emoji:
                                            payload.giftEmoji,

                                        label:
                                            payload.giftLabel

                                    });


                                    return;
                                }

                                // =====================================
                                // VIEWER COUNT
                                // =====================================

                                if (
                                    payload.type ===
                                    'VIEWER_COUNT'
                                ) {

                                    const count =
                                        Number(
                                            payload.viewerCount ?? 0
                                        );


                                    setViewerCount(
                                        count
                                    );


                                    console.log(
                                        '👁 Viewer count received:',
                                        count
                                    );


                                    return;
                                }

                                // =====================================
                                // HOST REMOVED VIEWER
                                // =====================================

                                if (
                                    payload.type ===
                                    'KICK_VIEWER' &&
                                    String(payload.toUserId) ===
                                    String(currentUser.id)
                                ) {

                                    console.log(
                                        '🚫 Host removed this viewer'
                                    );


                                    if (
                                        peerConnectionRef.current
                                    ) {

                                        try {

                                            peerConnectionRef
                                                .current
                                                .close();

                                        } catch (error) {

                                            console.warn(
                                                'Unable to close peer:',
                                                error
                                            );
                                        }


                                        peerConnectionRef.current =
                                            null;
                                    }


                                    pendingCandidatesRef.current =
                                        [];


                                    if (
                                        remoteVideoRef.current
                                    ) {

                                        remoteVideoRef
                                            .current
                                            .srcObject =
                                            null;
                                    }


                                    setVideoConnected(
                                        false
                                    );


                                    setViewerCount(
                                        0
                                    );


                                    navigate(
                                        '/live',
                                        {
                                            replace: true
                                        }
                                    );


                                    return;
                                }


                                // =====================================
                                // HOST REMOVED GUEST PARTICIPATION
                                // =====================================

                                if (
                                    payload.type === 'GUEST_REMOVED' &&
                                    String(payload.toUserId) ===
                                    String(currentUser.id)
                                ) {

                                    console.log(
                                        'ℹ️ Host removed guest participation'
                                    );

                                    await handleLeaveGuestMode();

                                    return;
                                }


                                // =====================================
                                // HOST ACCEPTED GUEST REQUEST
                                // =====================================

                                if (
                                    payload.type === 'GUEST_ACCEPTED' &&
                                    String(payload.toUserId) ===
                                    String(currentUser.id)
                                ) {

                                    console.log(
                                        '✅ Host accepted guest request'
                                    );


                                    setGuestRequestStatus(
                                        'accepted'
                                    );


                                    const peer =
                                        peerConnectionRef.current;


                                    if (!peer) {

                                        console.error(
                                            '❌ Guest peer connection is missing'
                                        );

                                        setGuestRequestStatus(
                                            'idle'
                                        );

                                        return;
                                    }


                                    const guestStream =
                                        await startGuestMedia();


                                    if (!guestStream) {

                                        setGuestRequestStatus(
                                            'idle'
                                        );


                                        if (
                                            stompClientRef.current
                                                ?.connected
                                        ) {

                                            stompClientRef.current
                                                .publish({

                                                    destination:
                                                        `/app/live/${live.id}`,

                                                    body:
                                                        JSON.stringify({

                                                            liveId:
                                                                live.id,

                                                            fromUserId:
                                                                currentUser.id,

                                                            toUserId:
                                                                live.hostId,

                                                            type:
                                                                'GUEST_MEDIA_FAILED'

                                                        })
                                                });
                                        }

                                        return;
                                    }


                                    // =====================================
                                    // ADD GUEST CAMERA + MICROPHONE
                                    // =====================================

                                    guestStream
                                        .getTracks()
                                        .forEach(
                                            track => {

                                                const alreadyAdded =
                                                    peer
                                                        .getSenders()
                                                        .some(
                                                            sender =>
                                                                sender.track?.id ===
                                                                track.id
                                                        );


                                                if (!alreadyAdded) {

                                                    peer.addTrack(
                                                        track,
                                                        guestStream
                                                    );
                                                }
                                            }
                                        );


                                    // =====================================
                                    // RENEGOTIATION OFFER FROM GUEST
                                    // =====================================

                                    const offer =
                                        await peer.createOffer();


                                    await peer
                                        .setLocalDescription(
                                            offer
                                        );


                                    if (
                                        stompClientRef.current
                                            ?.connected
                                    ) {

                                        stompClientRef.current
                                            .publish({

                                                destination:
                                                    `/app/live/${live.id}`,

                                                body:
                                                    JSON.stringify({

                                                        liveId:
                                                            live.id,

                                                        fromUserId:
                                                            currentUser.id,

                                                        toUserId:
                                                            live.hostId,

                                                        type:
                                                            'GUEST_OFFER',

                                                        sdp:
                                                            peer
                                                                .localDescription
                                                                ?.sdp ||
                                                            offer.sdp
                                                    })
                                            });


                                        console.log(
                                            '📤 Guest media OFFER sent'
                                        );
                                    }


                                    return;
                                }

                                // =====================================
                                // GUEST REQUEST REJECTED
                                // =====================================

                                if (
                                    payload.type === 'GUEST_REJECTED' &&
                                    String(payload.toUserId) ===
                                    String(currentUser.id)
                                ) {

                                    console.log(
                                        '❌ Host rejected guest request'
                                    );

                                    setGuestRequestStatus(
                                        'rejected'
                                    );

                                    /*
                                     * Important:
                                     * le viewer RESTE dans le live.
                                     * On ne ferme PAS peerConnectionRef.
                                     * On ne fait PAS navigate('/live').
                                     */

                                    window.setTimeout(
                                        () => {
                                            setGuestRequestStatus(
                                                'idle'
                                            );
                                        },
                                        2500
                                    );

                                    return;
                                }


                                // =====================================
                                // OFFER
                                // =====================================

                                if (
                                    payload.type ===
                                    'OFFER'
                                ) {

                                    console.log(
                                        '📥 Viewer received OFFER'
                                    );


                                    if (
                                        peerConnectionRef.current
                                    ) {

                                        peerConnectionRef
                                            .current
                                            .close();

                                        peerConnectionRef.current =
                                            null;
                                    }


                                    setVideoConnected(
                                        false
                                    );

                                    setViewerCount(
                                        0
                                    );


                                    const peer =
                                        new RTCPeerConnection(
                                            RTC_CONFIGURATION
                                        );


                                    peerConnectionRef.current =
                                        peer;


                                    // =====================================
                                    // SEND VIEWER ICE
                                    // =====================================

                                    peer.onicecandidate =
                                        (event) => {

                                            if (
                                                !event.candidate ||
                                                !stompClientRef
                                                    .current
                                                    ?.connected
                                            ) {

                                                return;
                                            }


                                            stompClientRef
                                                .current
                                                .publish({

                                                    destination:
                                                        `/app/live/${live.id}`,

                                                    body:
                                                        JSON.stringify(
                                                            {
                                                                liveId:
                                                                    live.id,

                                                                fromUserId:
                                                                    currentUser.id,

                                                                toUserId:
                                                                    live.hostId,

                                                                type:
                                                                    'CANDIDATE',

                                                                candidate:
                                                                    event
                                                                        .candidate
                                                                        .toJSON()
                                                            }
                                                        )
                                                });


                                            console.log(
                                                '🧊 Viewer ICE sent'
                                            );
                                        };


                                    peer.ontrack =
                                        async (event) => {

                                            console.log(
                                                '🎥 Host media received:',
                                                {
                                                    kind:
                                                        event.track?.kind,

                                                    id:
                                                        event.track?.id,

                                                    streams:
                                                        event.streams?.length || 0
                                                }
                                            );


                                            // =====================================
                                            // KEEP THE REMOTE STREAM EVEN IF
                                            // <video> IS NOT READY YET
                                            // =====================================

                                            let remoteStream =
                                                remoteStreamRef.current;


                                            if (
                                                event.streams?.[0]
                                            ) {

                                                remoteStream =
                                                    event.streams[0];

                                                remoteStreamRef.current =
                                                    remoteStream;

                                            } else {

                                                if (!remoteStream) {

                                                    remoteStream =
                                                        new MediaStream();

                                                    remoteStreamRef.current =
                                                        remoteStream;
                                                }


                                                const trackAlreadyExists =
                                                    remoteStream
                                                        .getTracks()
                                                        .some(
                                                            track =>
                                                                track.id ===
                                                                event.track.id
                                                        );


                                                if (!trackAlreadyExists) {

                                                    remoteStream.addTrack(
                                                        event.track
                                                    );
                                                }
                                            }


                                            // =====================================
                                            // ATTACH STREAM TO VIDEO
                                            // VIDEO MAY NOT BE READY IMMEDIATELY
                                            // =====================================

                                            const attachRemoteVideo =
                                                async (attempt = 0) => {

                                                    const video =
                                                        remoteVideoRef.current;


                                                    if (!video) {

                                                        if (attempt < 20) {

                                                            window.setTimeout(
                                                                () => {
                                                                    attachRemoteVideo(
                                                                        attempt + 1
                                                                    );
                                                                },
                                                                100
                                                            );

                                                        } else {

                                                            console.error(
                                                                '❌ Remote video element still missing after retry'
                                                            );
                                                        }

                                                        return;
                                                    }


                                                    if (
                                                        video.srcObject !==
                                                        remoteStream
                                                    ) {

                                                        video.srcObject =
                                                            remoteStream;
                                                    }


                                                    try {

                                                        video.muted =
                                                            false;

                                                        await video.play();

                                                        setVideoConnected(
                                                            true
                                                        );

                                                        setNeedsTapToPlay(
                                                            false
                                                        );


                                                        console.log(
                                                            '✅ Reconnected live video/audio displayed'
                                                        );

                                                    } catch (playError) {

                                                        console.warn(
                                                            '⚠️ Autoplay blocked after reconnect:',
                                                            playError
                                                        );


                                                        try {

                                                            video.muted =
                                                                true;

                                                            await video.play();

                                                            setVideoConnected(
                                                                true
                                                            );

                                                            setNeedsTapToPlay(
                                                                true
                                                            );

                                                        } catch (mutedError) {

                                                            console.error(
                                                                '❌ Unable to resume remote video:',
                                                                mutedError
                                                            );

                                                            setVideoConnected(
                                                                false
                                                            );

                                                            setNeedsTapToPlay(
                                                                true
                                                            );
                                                        }
                                                    }
                                                };


                                            attachRemoteVideo();
                                        };
                                    // =====================================
                                    // CONNECTION STATE
                                    // =====================================

                                    peer.onconnectionstatechange =
                                        () => {

                                            const state =
                                                peer.connectionState;

                                            console.log(
                                                '🌐 Viewer WebRTC state:',
                                                state
                                            );


                                            if (
                                                state === 'connected'
                                            ) {

                                                setVideoConnected(
                                                    true
                                                );

                                                return;
                                            }


                                            if (
                                                state === 'disconnected'
                                            ) {

                                                console.warn(
                                                    '⚠️ WebRTC temporarily disconnected'
                                                );

                                                setVideoConnected(
                                                    false
                                                );


                                                window.setTimeout(
                                                    () => {

                                                        if (
                                                            peer.connectionState !==
                                                            'disconnected'
                                                        ) {
                                                            return;
                                                        }


                                                        // If WebSocket also disconnected,
                                                        // STOMP onConnect will send JOIN.
                                                        // Do NOT also send RECONNECT_REQUEST.
                                                        if (
                                                            stompWasDisconnectedRef.current
                                                        ) {

                                                            console.log(
                                                                '🔌 Waiting for STOMP reconnect — JOIN will rebuild WebRTC'
                                                            );

                                                            return;
                                                        }


                                                        console.warn(
                                                            '🔄 Requesting WebRTC reconnection'
                                                        );


                                                        if (
                                                            stompClientRef.current
                                                                ?.connected
                                                        ) {

                                                            stompClientRef.current.publish({

                                                                destination:
                                                                    `/app/live/${live.id}`,

                                                                body:
                                                                    JSON.stringify({

                                                                        liveId:
                                                                            live.id,

                                                                        fromUserId:
                                                                            currentUser.id,

                                                                        toUserId:
                                                                            live.hostId,

                                                                        type:
                                                                            'RECONNECT_REQUEST'
                                                                    })
                                                            });
                                                        }

                                                    },
                                                    3000
                                                );


                                                return;
                                            }


                                            if (
                                                state === 'failed'
                                            ) {

                                                console.error(
                                                    '❌ WebRTC connection failed'
                                                );

                                                setVideoConnected(
                                                    false
                                                );


                                                if (
                                                    stompClientRef.current?.connected &&
                                                    !stompWasDisconnectedRef.current
                                                ) {

                                                    stompClientRef.current.publish({

                                                        destination:
                                                            `/app/live/${live.id}`,

                                                        body:
                                                            JSON.stringify({

                                                                liveId:
                                                                    live.id,

                                                                fromUserId:
                                                                    currentUser.id,

                                                                toUserId:
                                                                    live.hostId,

                                                                type:
                                                                    'RECONNECT_REQUEST'
                                                            })
                                                    });
                                                }

                                                return;
                                            }


                                            if (
                                                state === 'closed'
                                            ) {

                                                setVideoConnected(
                                                    false
                                                );
                                            }
                                        };


                                    peer.oniceconnectionstatechange =
                                        () => {

                                            const state =
                                                peer.iceConnectionState;

                                            console.log(
                                                '🧊 Viewer ICE state:',
                                                state
                                            );


                                            if (
                                                state === 'failed'
                                            ) {

                                                console.error(
                                                    '❌ ICE FAILED — direct WebRTC connection unavailable'
                                                );

                                                setVideoConnected(false);
                                            }


                                            if (
                                                state === 'disconnected'
                                            ) {

                                                console.warn(
                                                    '⚠️ ICE temporarily disconnected'
                                                );
                                            }


                                            if (
                                                state === 'connected' ||
                                                state === 'completed'
                                            ) {

                                                console.log(
                                                    '✅ ICE connection established'
                                                );
                                            }
                                        };




                                    // =====================================
                                    // ACCEPT OFFER
                                    // =====================================

                                    await peer
                                        .setRemoteDescription(
                                            {
                                                type:
                                                    'offer',

                                                sdp:
                                                    payload.sdp
                                            }
                                        );


                                    // =====================================
                                    // APPLY QUEUED ICE
                                    // =====================================

                                    const queuedCandidates =
                                        [
                                            ...pendingCandidatesRef
                                                .current
                                        ];


                                    pendingCandidatesRef.current =
                                        [];


                                    for (
                                        const candidate
                                        of queuedCandidates
                                    ) {

                                        try {

                                            await peer
                                                .addIceCandidate(
                                                    new RTCIceCandidate(
                                                        candidate
                                                    )
                                                );

                                        } catch (
                                        candidateError
                                        ) {

                                            console.warn(
                                                'Unable to add queued ICE:',
                                                candidateError
                                            );
                                        }
                                    }


                                    // =====================================
                                    // CREATE ANSWER
                                    // =====================================

                                    const answer =
                                        await peer
                                            .createAnswer();


                                    await peer
                                        .setLocalDescription(
                                            answer
                                        );


                                    // =====================================
                                    // SEND ANSWER
                                    // =====================================

                                    if (
                                        stompClientRef
                                            .current
                                            ?.connected
                                    ) {

                                        stompClientRef
                                            .current
                                            .publish({

                                                destination:
                                                    `/app/live/${live.id}`,

                                                body:
                                                    JSON.stringify(
                                                        {
                                                            liveId:
                                                                live.id,

                                                            fromUserId:
                                                                currentUser.id,

                                                            toUserId:
                                                                live.hostId,

                                                            type:
                                                                'ANSWER',

                                                            sdp:
                                                                peer
                                                                    .localDescription
                                                                    ?.sdp ||
                                                                answer.sdp
                                                        }
                                                    )
                                            });


                                        console.log(
                                            '📤 Viewer sent ANSWER'
                                        );
                                    }


                                    return;
                                }


                                // =====================================
                                // GUEST RENEGOTIATION ANSWER
                                // =====================================

                                if (
                                    payload.type === 'GUEST_ANSWER' &&
                                    String(payload.toUserId) ===
                                    String(currentUser.id)
                                ) {

                                    const peer =
                                        peerConnectionRef.current;


                                    if (
                                        !peer ||
                                        !payload.sdp
                                    ) {
                                        return;
                                    }


                                    console.log(
                                        '📥 Guest ANSWER received'
                                    );


                                    await peer
                                        .setRemoteDescription(
                                            new RTCSessionDescription({

                                                type:
                                                    'answer',

                                                sdp:
                                                    payload.sdp
                                            })
                                        );


                                    console.log(
                                        '🎉 Guest camera/microphone connected'
                                    );


                                    return;
                                }

                                // =====================================
                                // GUEST LEAVE RENEGOTIATION ANSWER
                                // =====================================

                                if (
                                    payload.type === 'GUEST_LEAVE_ANSWER' &&
                                    String(payload.toUserId) ===
                                    String(currentUser.id)
                                ) {

                                    const peer =
                                        peerConnectionRef.current;

                                    if (
                                        !peer ||
                                        !payload.sdp
                                    ) {
                                        return;
                                    }

                                    console.log(
                                        '📥 Guest leave ANSWER received'
                                    );

                                    await peer
                                        .setRemoteDescription(
                                            new RTCSessionDescription({
                                                type:
                                                    'answer',

                                                sdp:
                                                    payload.sdp
                                            })
                                        );

                                    console.log(
                                        '✅ Returned to viewer mode'
                                    );

                                    return;
                                }


                                // =====================================
                                // HOST ICE
                                // =====================================

                                if (
                                    payload.type ===
                                    'CANDIDATE' &&
                                    payload.candidate
                                ) {

                                    const peer =
                                        peerConnectionRef
                                            .current;


                                    if (
                                        peer &&
                                        peer.remoteDescription
                                    ) {

                                        try {

                                            await peer
                                                .addIceCandidate(
                                                    new RTCIceCandidate(
                                                        payload.candidate
                                                    )
                                                );


                                            console.log(
                                                '🧊 Viewer added host ICE'
                                            );


                                        } catch (
                                        candidateError
                                        ) {

                                            console.error(
                                                'Unable to add host ICE:',
                                                candidateError
                                            );
                                        }

                                    } else {

                                        pendingCandidatesRef
                                            .current
                                            .push(
                                                payload.candidate
                                            );
                                    }


                                    return;
                                }


                                // =====================================
                                // HOST ENDED LIVE
                                // =====================================

                                if (
                                    payload.type ===
                                    'END'
                                ) {

                                    console.log(
                                        '⚫ Host ended live'
                                    );


                                    // =====================================
                                    // STOP GUEST CAMERA + MICROPHONE
                                    // WHEN HOST ENDS LIVE
                                    // =====================================

                                    if (
                                        guestStreamRef.current
                                    ) {

                                        guestStreamRef.current
                                            .getTracks()
                                            .forEach(
                                                track => {
                                                    track.stop();
                                                }
                                            );

                                        guestStreamRef.current =
                                            null;
                                    }


                                    if (
                                        guestLocalVideoRef.current
                                    ) {

                                        guestLocalVideoRef.current
                                            .srcObject =
                                            null;
                                    }


                                    setGuestRequestStatus(
                                        'idle'
                                    );

                                    setGuestCameraEnabled(
                                        false
                                    );

                                    setGuestMicEnabled(
                                        false
                                    );

                                    setGuestPreviewDragging(
                                        false
                                    );

                                    setGuestPreviewPosition({
                                        x: null,
                                        y: null
                                    });


                                    if (
                                        peerConnectionRef
                                            .current
                                    ) {

                                        peerConnectionRef
                                            .current
                                            .close();

                                        peerConnectionRef.current =
                                            null;
                                    }


                                    pendingCandidatesRef.current =
                                        [];


                                    if (
                                        remoteVideoRef.current
                                    ) {

                                        remoteVideoRef
                                            .current
                                            .srcObject =
                                            null;
                                    }


                                    setVideoConnected(
                                        false
                                    );


                                    setLive(
                                        (previous) => {

                                            if (!previous) {

                                                return previous;
                                            }


                                            return {
                                                ...previous,
                                                status:
                                                    'ENDED'
                                            };
                                        }
                                    );


                                    fetchLive();

                                    return;
                                }


                            } catch (
                            signalError
                            ) {

                                console.error(
                                    '❌ Invalid live signal:',
                                    signalError
                                );
                            }

                        }
                    );


                    // =====================================
                    // SEND JOIN
                    // =====================================

                    client.publish({

                        destination:
                            `/app/live/${live.id}`,

                        body:
                            JSON.stringify(
                                {
                                    liveId:
                                        live.id,

                                    fromUserId:
                                        currentUser.id,

                                    toUserId:
                                        live.hostId,

                                    type:
                                        'JOIN'
                                }
                            )
                    });


                    console.log(
                        '📤 JOIN sent:',


                        {
                            liveId:
                                live.id,

                            fromUserId:
                                currentUser.id,

                            toUserId:
                                live.hostId
                        }
                    );

                    // =====================================
                    // REQUEST CURRENT VIEWER COUNT
                    // =====================================

                    window.setTimeout(() => {

                        if (!client.connected) {
                            return;
                        }

                        client.publish({
                            destination:
                                `/app/live/${live.id}`,

                            body: JSON.stringify({
                                liveId:
                                    live.id,

                                fromUserId:
                                    currentUser.id,

                                toUserId:
                                    live.hostId,

                                type:
                                    'VIEWER_COUNT_REQUEST'
                            })
                        });

                        console.log(
                            '👁 VIEWER_COUNT_REQUEST sent'
                        );

                    }, 500);
                },





                onStompError:
                    (frame) => {

                        console.error(
                            '❌ Viewer STOMP error:',
                            frame
                                .headers
                                ?.message
                        );
                    },


                onWebSocketError:
                    (event) => {

                        console.error(
                            '❌ Viewer WebSocket error:',
                            event
                        );
                    }
            });


        stompClientRef.current =
            client;


        client.activate();


        // =====================================
        // CLEANUP
        // =====================================

        return () => {

            console.log(
                '🧹 Cleaning viewer connection'
            );


            if (
                client.connected
            ) {

                try {

                    client.publish({

                        destination:
                            `/app/live/${live.id}`,

                        body:
                            JSON.stringify(
                                {
                                    liveId:
                                        live.id,

                                    fromUserId:
                                        currentUser.id,

                                    toUserId:
                                        live.hostId,

                                    type:
                                        'LEAVE'
                                }
                            )
                    });

                } catch (
                leaveError
                ) {

                    console.warn(
                        'Unable to send LEAVE:',
                        leaveError
                    );
                }
            }


            if (
                peerConnectionRef.current
            ) {

                try {

                    peerConnectionRef
                        .current
                        .close();

                } catch (
                closeError
                ) {

                    console.warn(
                        'Unable to close peer:',
                        closeError
                    );
                }


                peerConnectionRef.current =
                    null;
            }


            pendingCandidatesRef.current =
                [];


            if (
                remoteVideoElement
            ) {

                remoteVideoElement
                    .srcObject =
                    null;
            }


            client.deactivate();


            stompClientRef.current =
                null;
        };


    }, [
        live?.id,
        live?.hostId,
        live?.status,
        currentUser?.id,
        token,
        authorizationHeader,
        fetchLive,
        navigate,
        addLiveReaction,
        addLiveComment,
        addLiveGift,
        startGuestMedia,
        handleLeaveGuestMode

    ]);


    // =====================================
    // LOADING
    // =====================================

    if (loading) {

        return (

            <main className="live-viewer-page">

                <div className="live-viewer-state">

                    {t(
                        'common.loading',
                        'Loading...'
                    )}

                </div>

            </main>
        );
    }


    // =====================================
    // ERROR
    // =====================================

    if (
        error ||
        !live
    ) {

        return (

            <main className="live-viewer-page">

                <div className="live-viewer-state">

                    <p>
                        {error}
                    </p>


                    <button
                        type="button"
                        onClick={() =>
                            navigate('/live')
                        }
                    >

                        {t(
                            'common.back',
                            'Back'
                        )}

                    </button>

                </div>

            </main>
        );
    }


    const ended =
        String(
            live.status
        ).toUpperCase() ===
        'ENDED';


    return (

        <main className="live-viewer-page">


            <header className="live-viewer-header">

                <button
                    type="button"
                    onClick={() =>
                        navigate('/live')
                    }
                    aria-label={t(
                        'common.back',
                        'Back'
                    )}
                >
                    <FaArrowLeft />
                </button>

            </header>

            <section
                ref={liveVideoContainerRef}
                className="live-viewer-video"
            >

                {!ended && (

                    <button
                        type="button"
                        className="live-viewer-leave-button"
                        onClick={handleLeaveLive}
                    >
                        <FaSignOutAlt />

                        {t('live.leave', 'Leave')}
                    </button>
                )}


                {!ended &&
                    guestRequestStatus !== 'accepted' && (

                        <button
                            type="button"
                            className="live-request-join-button"
                            onClick={handleRequestToJoin}
                            disabled={
                                guestRequestStatus === 'pending'
                            }
                        >
                            <FaVideo />

                            <span>
                                {guestRequestStatus === 'pending'
                                    ? t(
                                        'live.guestRequestPending',
                                        'Request sent'
                                    )
                                    : guestRequestStatus === 'accepted'
                                        ? t(
                                            'live.guestRequestAccepted',
                                            'Accepted'
                                        )
                                        : t(
                                            'live.requestToJoin',
                                            'Request to join'
                                        )
                                }
                            </span>
                        </button>
                    )
                }

                <div className="live-viewer-interactions">

                    <form
                        className="live-comment-form"
                        onSubmit={handleSendComment}
                    >

                        <FaCommentDots
                            className="live-comment-icon"
                        />


                        <input
                            type="text"
                            value={commentText}
                            maxLength={120}
                            placeholder={t("live.commentPlaceholder", "Say something...")}
                            onChange={
                                event =>
                                    setCommentText(
                                        event.target.value
                                    )
                            }
                        />


                        <button
                            type="submit"
                            className="live-comment-send"
                            disabled={
                                !commentText.trim()
                            }
                            aria-label={t("live.sendComment", "Send comment")}
                        >
                            <FaPaperPlane />
                        </button>

                    </form>


                    <button
                        type="button"
                        className="live-interaction-button gift"
                        onClick={() =>
                            setShowGiftPicker(
                                previous => !previous
                            )
                        }
                        aria-label={t("live.sendGift", "Send gift")}
                    >
                        <FaGift />
                    </button>


                    <button
                        type="button"
                        className="live-interaction-button heart"
                        onClick={handleSendHeart}
                        aria-label={t("live.sendHeart", "Send heart")}
                    >
                        <FaHeart />
                    </button>



                </div>




                {!ended && showGiftPicker && (

                    <div className="live-gift-picker">

                        <div className="live-gift-picker-header">

                            <div>
                                <strong>{t("live.sendGift", "Send a Gift")}</strong>
                                <span>{t("live.giftSubtitle", "Make the moment special")}</span>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setShowGiftPicker(false)
                                }
                            >
                                <FaTimes />
                            </button>

                        </div>


                        {/* WALLET */}

                        <div className="live-gift-wallet">

                            <span>
                                {t("live.yourBalance", "Your balance")}
                            </span>

                            <strong>
                                🪙 {
                                    walletLoading
                                        ? '...'
                                        : coinBalance
                                }
                            </strong>

                        </div>


                        {/* GIFTS */}

                        <div className="live-gift-grid">

                            {LIVE_GIFTS.map(
                                gift => (

                                    <button
                                        key={gift.code}
                                        type="button"

                                        className={
                                            `live-gift-option ${coinBalance < gift.price
                                                ? 'is-low-balance'
                                                : ''
                                            }`
                                        }

                                        disabled={
                                            sendingGiftCode !== null
                                        }

                                        onClick={() =>
                                            handleSendGift(gift)
                                        }
                                    >

                                        <span className="live-gift-option-emoji">
                                            {gift.emoji}
                                        </span>

                                        <strong>
                                            {gift.label}
                                        </strong>

                                        <small>
                                            🪙 {gift.price}
                                        </small>

                                    </button>

                                )
                            )}

                        </div>


                        {giftError && (

                            <div className="live-gift-error">

                                <span>
                                    {giftError}
                                </span>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowCoinModal(true)
                                    }
                                >
                                    {t("live.buyCoins", "Buy Coins")}
                                </button>

                            </div>
                        )}

                    </div>
                )}

                <video
                    ref={remoteVideoRef}
                    className="live-viewer-remote-video"
                    autoPlay
                    playsInline
                />

                {guestRequestStatus === 'accepted' && (

                    <div
                        ref={guestPreviewRef}
                        className={`guest-self-preview ${guestPreviewDragging
                            ? 'is-dragging'
                            : ''
                            }`}
                        style={
                            guestPreviewPosition.x != null &&
                                guestPreviewPosition.y != null
                                ? {
                                    left:
                                        `${guestPreviewPosition.x}px`,

                                    top:
                                        `${guestPreviewPosition.y}px`,

                                    right:
                                        'auto'
                                }
                                : undefined
                        }
                    >
                        <video
                            ref={guestLocalVideoRef}
                            className="guest-self-video"
                            autoPlay
                            muted
                            playsInline
                        />

                        <div className="guest-self-controls">

                            <button
                                type="button"
                                className={
                                    guestMicEnabled
                                        ? ''
                                        : 'is-off'
                                }
                                onClick={
                                    handleToggleGuestMicrophone
                                }
                                aria-label={
                                    t(
                                        'live.toggleMicrophone',
                                        'Toggle microphone'
                                    )
                                }
                            >
                                {guestMicEnabled
                                    ? <FaMicrophone />
                                    : <FaMicrophoneSlash />
                                }
                            </button>


                            <button
                                type="button"
                                className={
                                    guestCameraEnabled
                                        ? ''
                                        : 'is-off'
                                }
                                onClick={
                                    handleToggleGuestCamera
                                }
                                aria-label={
                                    t(
                                        'live.toggleCamera',
                                        'Toggle camera'
                                    )
                                }
                            >
                                {guestCameraEnabled
                                    ? <FaVideo />
                                    : <FaVideoSlash />
                                }
                            </button>


                            <button
                                type="button"
                                className="leave-guest"
                                onClick={
                                    handleLeaveGuestMode
                                }
                                aria-label={
                                    t(
                                        'live.leaveGuestMode',
                                        'Leave guest mode'
                                    )
                                }
                            >
                                <FaSignOutAlt />
                            </button>

                        </div>

                        <div
                            className="guest-self-live-badge guest-preview-drag-handle"

                            onMouseDown={
                                event => {

                                    handleGuestPreviewDragStart(
                                        event.clientX,
                                        event.clientY
                                    );
                                }
                            }

                            onTouchStart={
                                event => {

                                    if (
                                        !event.touches?.length
                                    ) {
                                        return;
                                    }

                                    handleGuestPreviewDragStart(
                                        event.touches[0].clientX,
                                        event.touches[0].clientY
                                    );
                                }
                            }
                        >                            {t(
                            'live.youAreGuest',
                            'live'
                        )}
                        </div>

                    </div>
                )}

                {/* =====================================
                        LIVE COMMENTS
                    ===================================== */}

                <div className="live-comments-layer">

                    {liveComments.map(
                        comment => (

                            <div
                                key={comment.id}
                                className="live-comment-item"
                            >

                                <div className="live-comment-avatar">
                                    {comment.userName
                                        ?.charAt(0)
                                        ?.toUpperCase() ||
                                        'U'}
                                </div>


                                <div className="live-comment-content">

                                    <strong>
                                        {comment.userName}
                                    </strong>

                                    <span>
                                        {comment.message}
                                    </span>

                                </div>

                            </div>

                        )
                    )}

                </div>

                <div className="live-gifts-layer">

                    {liveGifts.map(
                        gift => (

                            <div
                                key={gift.id}
                                className="live-gift-animation"
                            >

                                <span className="live-gift-big-emoji">
                                    {gift.emoji}
                                </span>

                                <div>
                                    <strong>
                                        {gift.userName}
                                    </strong>

                                    <span>
                                        {t("live.sentGift", "sent {{gift}}", { gift: gift.label })}
                                    </span>
                                </div>

                            </div>

                        )
                    )}

                </div>

                {!ended && needsTapToPlay && (

                    <button
                        type="button"
                        className="live-viewer-tap-play"
                        onClick={startRemotePlayback}
                    >
                        <FaBroadcastTower />

                        <span>
                            {t("live.tapToWatch", "Tap to watch Live")}
                        </span>
                    </button>

                )}


                {!ended && (
                    <div className="live-viewer-host-bottom">
                        <strong>
                            {live.hostName}
                        </strong>

                        {live.title && (
                            <span>
                                {live.title}
                            </span>
                        )}
                    </div>
                )}


                {!ended && (
                    <span className="live-viewer-badge">
                        <span />
                        {t("live.badge", "LIVE")}
                    </span>
                )}

                {!ended && (

                    <div className="live-viewer-count">

                        <FaEye />

                        <span>
                            {viewerCount}
                        </span>

                    </div>
                )}

                {!ended && !videoConnected && !needsTapToPlay && (
                    <div className="live-viewer-connecting">

                        <FaBroadcastTower />

                        <h2>
                            {t(
                                'live.connectingToLive',
                                'Connecting to live video...'
                            )}
                        </h2>

                        <p>
                            {t(
                                'live.pleaseWait',
                                'Please wait while we connect you to the live stream.'
                            )}
                        </p>

                    </div>
                )}

                {ended && (
                    <div className="live-viewer-ended">

                        <h2>
                            {t(
                                'live.liveEnded',
                                'Live ended'
                            )}
                        </h2>

                        <p>
                            {t(
                                'live.liveNoLongerAvailable',
                                'This live session is no longer available.'
                            )}
                        </p>

                    </div>
                )}

                <div
                    className="live-reactions-layer"
                    aria-hidden="true"
                >

                    {liveReactions.map(
                        (reaction) => (

                            <div
                                key={reaction.id}
                                className="live-floating-reaction"
                                style={{
                                    left:
                                        `${reaction.left}px`,

                                    animationDuration:
                                        `${reaction.duration}s`
                                }}
                            >
                                <strong>
                                    {reaction.name}
                                </strong>

                                <FaHeart />
                            </div>

                        )
                    )}

                </div>

            </section>

            {showCoinModal && (

                <div
                    className="coin-modal-backdrop"
                    onClick={() =>
                        setShowCoinModal(false)
                    }
                >

                    <div
                        className="coin-modal"
                        onClick={
                            event =>
                                event.stopPropagation()
                        }
                    >

                        {/* =====================================
                CLOSE
            ===================================== */}

                        <button
                            type="button"
                            className="coin-modal-close"
                            onClick={() =>
                                setShowCoinModal(false)
                            }
                            aria-label="Close"
                        >
                            <FaTimes />
                        </button>


                        {/* =====================================
                TITLE
            ===================================== */}

                        <div className="coin-modal-header">

                            <div className="coin-modal-coin">
                                🪙
                            </div>


                            <h2>
                                Get HalalHeart Coins
                            </h2>


                            <p>
                                Buy coins to send gifts
                                during Live.
                            </p>

                        </div>


                        {/* =====================================
                CURRENT BALANCE
            ===================================== */}

                        <div className="coin-modal-balance">

                            <span>
                                Current balance
                            </span>

                            <strong>
                                🪙 {coinBalance}
                            </strong>

                        </div>


                        {/* =====================================
                PACKAGES
            ===================================== */}

                        <div className="coin-package-grid">

                            {COIN_PACKAGES.map(
                                coinPackage => (

                                    <button
                                        key={
                                            coinPackage.code
                                        }
                                        type="button"

                                        className="coin-package-option"

                                        disabled={
                                            buyingPackage !==
                                            null
                                        }

                                        onClick={() =>
                                            handleBuyCoins(
                                                coinPackage
                                            )
                                        }
                                    >

                                        <span className="coin-package-coins">

                                            🪙 {
                                                coinPackage.coins
                                            }

                                        </span>


                                        <strong>
                                            {
                                                coinPackage.price
                                            }
                                        </strong>


                                        {buyingPackage ===
                                            coinPackage.code && (

                                                <small>
                                                    Opening Stripe...
                                                </small>
                                            )}

                                    </button>

                                )
                            )}

                        </div>


                        {giftError && (

                            <div className="coin-modal-error">

                                {giftError}

                            </div>
                        )}

                    </div>

                </div>
            )}

        </main>
    );
}

// const LIVE_GIFTS = [
//     {
//         code: 'rose',
//         emoji: '🌹',
//         label: 'Rose'
//     },
//     {
//         code: 'love',
//         emoji: '💖',
//         label: 'Love'
//     },
//     {
//         code: 'star',
//         emoji: '⭐',
//         label: 'Star'
//     },
//     {
//         code: 'diamond',
//         emoji: '💎',
//         label: 'Diamond'
//     },
//     {
//         code: 'crown',
//         emoji: '👑',
//         label: 'Crown'
//     },
//     {
//         code: 'ring',
//         emoji: '💍',
//         label: 'Ring'
//     }
// ];

export default LiveViewerPage;